/**
 * Telugu (India) locale for devices-ui.
 * Export is the "devices" subtree: host apps merge this into their i18n
 * resources as translation.devices so that t('devices.list.heading') etc. resolve.
 */
import type { DevicesLocale } from './en'

export const devicesTeIn = {
  // ---------------------------------------------------------------------------
  // Common / shared labels
  // ---------------------------------------------------------------------------
  common: {
    cancel: `రద్దు చేయండి`,
    delete: `తొలగించు`,
    save: `సేవ్ చేయండి`,
    saveAndReboot: `సేవ్ & రీబూట్ చేయండి`,
    actions: `చర్యలు`,
    error: `లోపం`,
    yes: `అవును`,
    no: `కాదు`,
    all: `అన్నీ`,
    unchanged: `మార్పు లేదు`,
    loading: `లోడ్ అవుతోంది...`,
    none: `— ఏదీ లేదు —`,
    backToList: `జాబితాకు తిరిగి వెళ్లండి`,
  },

  // ---------------------------------------------------------------------------
  // Options used across multiple components
  // ---------------------------------------------------------------------------
  options: {
    transportUdp: `UDP`,
    transportTcp: `TCP`,
    transportTls: `TLS`,
    jitterNormal: `సాధారణం`,
    jitterLow: `తక్కువ`,
    jitterHigh: `ఎక్కువ`,
    natNormal: `సాధారణం`,
    natStun: `STUN`,
    serverGroupPrimary: `ప్రాథమిక`,
    serverGroupSecondary: `ద్వితీయ`,
    sourceMango: `Mango కొత్త`,
    sourceRefurbished: `Mango పునరుద్ధరించబడిన`,
    sourceCustomer: `కస్టమర్`,
    wifiSecurityNone: `NONE`,
    wifiSecurityWep: `WEP`,
    wifiSecurityWpa: `WPA/WPA2 PSK`,
    wifiSecurityWpa3: `WPA3-Personal`,
    timezoneAlaska: `Alaska`,
    timezoneArizona: `Arizona`,
    timezoneAtlantic: `Atlantic Time (Canada)`,
    timezoneCentral: `Central Time (US & Canada)`,
    timezoneEastern: `Eastern Time (US & Canada)`,
    timezoneGreenwich: `Greenwich Mean Time (UK)`,
    timezoneHawaii: `Hawaii`,
    timezoneIndiana: `Indiana (East)`,
    timezoneMountain: `Mountain Time (US & Canada)`,
    timezonePacific: `Pacific Time (US & Canada)`,
    timezonePuertoRico: `Puerto Rico`,
  },

  // ---------------------------------------------------------------------------
  // Device form (add / edit)
  // ---------------------------------------------------------------------------
  form: {
    addDevice: `పరికరాన్ని జోడించండి`,
    editDevice: `పరికరాన్ని సవరించండి`,
    createDevice: `పరికరాన్ని సృష్టించండి`,
    loadingDevice: `పరికరం లోడ్ అవుతోంది...`,

    // Section headers
    deviceInfo: `పరికర సమాచారం`,
    advanced: `అధునాతన`,
    displaySettings: `ప్రదర్శన సెట్టింగ్‌లు`,

    // Field labels
    model: `మోడల్`,
    selectModel: `పరికర మోడల్‌ను ఎంచుకోండి…`,
    mostCommonYealink: `అత్యంత సాధారణ (Yealink)`,
    macAddress: `MAC చిరునామా`,
    macPlaceholder: `AA:BB:CC:DD:EE:FF`,
    generateRandomMac: `యాదృచ్ఛిక MAC చిరునామాను రూపొందించండి`,
    generateRandomMacConfirmTitle: `MAC చిరునామాను భర్తీ చేయాలా?`,
    generateRandomMacConfirmDescription: `యాదృచ్ఛిక MAC చిరునామాను రూపొందించడం ప్రస్తుత విలువను భర్తీ చేస్తుంది. మీరు ఖచ్చితంగా కొనసాగించాలనుకుంటున్నారా?`,
    generateRandomMacConfirmAction: `కొత్త MAC ను రూపొందించండి`,
    copyProvisioningMac: `ప్రొవిజనింగ్ URL కోసం MAC ను కాపీ చేయండి`,
    copyProvisioningMacHint: `చిన్న అక్షరాలలో, కోలన్‌లు లేకుండా కాపీ చేయండి (ప్రొవిజనింగ్ URL కోసం)`,
    copyProvisioningMacCopied: `{{value}} కాపీ చేయబడింది`,
    copyProvisioningMacFailed: `కాపీ చేయడం విఫలమైంది. విలువను స్వయంగా ఎంచుకోండి.`,
    checkingUniqueness: `ప్రత్యేకతను తనిఖీ చేస్తోంది…`,
    macRequired: `MAC చిరునామా అవసరం`,
    macInvalid: `MAC చిరునామా చెల్లదు`,
    macAlreadyInUse: `MAC చిరునామా ఇప్పటికే ఉపయోగంలో ఉంది`,
    modelRequired: `పరికర మోడల్ అవసరం.`,
    warrantyExpiresAt: `వారంటీ గడువు తేదీ`,
    source: `మూలం`,
    lastProvisionedTime: `చివరి ప్రావిజన్ సమయం`,
    lastProvisionedFrom: `చివరి ప్రావిజన్ నుండి`,
    assignedUser: `కేటాయించిన వినియోగదారు`,
    assignedUserRequired: `కేటాయించిన వినియోగదారు అవసరం.`,
    directory: `డైరెక్టరీ`,
    directoryRequired: `డైరెక్టరీ అవసరం.`,
    selectDirectory: `డైరెక్టరీని ఎంచుకోండి...`,
    primaryLineExtension: `ప్రాథమిక లైన్ ఎక్స్‌టెన్షన్`,
    noExtensionsForUser: `కేటాయించిన వినియోగదారుకు చెల్లుబాటు అయ్యే లైన్ ఎక్స్‌టెన్షన్లు లేవు. సేవ్ చేయడానికి ముందు వేరే వినియోగదారును ఎంచుకోండి లేదా లైన్ ఎక్స్‌టెన్షన్‌ను కేటాయించండి.`,
    primaryLineExtensionRequired: `ప్రాథమిక లైన్ ఎక్స్‌టెన్షన్ అవసరం.`,
    timeZone: `సమయ మండలం`,
    stationName: `స్టేషన్ పేరు`,
    stationNameRequired: `స్టేషన్ పేరు అవసరం.`,
    stationNamePlaceholder: `ఉదా. ముందు డెస్క్`,
    enableDaylightSavings: `డేలైట్ సేవింగ్స్‌ని ప్రారంభించండి`,
    cannotSaveTitle: `ఇంకా సేవ్ చేయలేరు`,
    buttonConfigurationInvalid: `సేవ్ చేయడానికి ముందు చెల్లని బటన్ కాన్ఫిగరేషన్‌ను సరిచేయండి.`,

    // Advanced section labels
    pages: `పేజీలు`,
    lineKeysAvailable: `లైన్ కీలు అందుబాటులో ఉన్నాయి`,
    selectModelFirst: `ముందుగా మోడల్‌ను ఎంచుకోండి...`,
    lineKeysHelper: `({{pages}} పేజీ{{pageSuffix}} x {{keysPerPage}} కీలు = {{total}} మొత్తం)`,
    allowProvisioning: `ప్రావిజనింగ్‌ను అనుమతించండి`,
    sidecarEnabled: `సైడ్‌కార్ ప్రారంభించబడింది`,
    transport: `ట్రాన్స్‌పోర్ట్`,
    jitter: `జిట్టర్`,
    serverGroupHost: `సర్వర్ గ్రూప్ హోస్ట్`,
    natTraversal: `NAT ట్రావర్సల్`,
    wifiEnabled: `WiFi ప్రారంభించబడింది`,
    wifiNetworkSsid: `WiFi నెట్‌వర్క్ SSID`,
    wifiSecurityMode: `WiFi సెక్యూరిటీ మోడ్`,
    wifiPassword: `WiFi పాస్‌వర్డ్`,
    acousticShieldEnabled: `ఎకౌస్టిక్ షీల్డ్ ప్రారంభించబడింది`,
  },

  // ---------------------------------------------------------------------------
  // Device preview card
  // ---------------------------------------------------------------------------
  preview: {
    title: `పరికర ప్రివ్యూ`,
    unavailable: `ఎంచుకున్న మోడల్ కోసం ప్రివ్యూ అందుబాటులో లేదు.`,
    emptyAlt: `ఖాళీ పరికర ప్రివ్యూ`,
  },

  // ---------------------------------------------------------------------------
  // Button grid
  // ---------------------------------------------------------------------------
  buttons: {
    linesAndButtons: `లైన్లు & బటన్లు`,
    sidecarButtons: `సైడ్‌కార్ బటన్లు`,
    lineButtonHelper_one: `{{modelName}} కోసం {{count}} బటన్ స్లాట్. వరుస 1 ఎల్లప్పుడూ లైన్ మరియు ప్రాథమిక లైన్ ఎక్స్‌టెన్షన్‌ను అనుసరిస్తుంది.`,

    lineButtonHelper_other: `{{modelName}} కోసం {{count}} బటన్ స్లాట్‌లు. వరుస 1 ఎల్లప్పుడూ లైన్ మరియు ప్రాథమిక లైన్ ఎక్స్‌టెన్షన్‌ను అనుసరిస్తుంది.`,
    sidecarButtonHelper_one: `{{count}} సైడ్‌కార్ స్లాట్.`,

    sidecarButtonHelper_other: `{{count}} సైడ్‌కార్ స్లాట్‌లు.`,
    noSlotsForModel: `ఈ మోడల్ కోసం బటన్ స్లాట్‌లు అందుబాటులో లేవు.`,
    selectModelForSlots: `బటన్ స్లాట్‌లను చూడటానికి పరికర మోడల్‌ను ఎంచుకోండి.`,
    columnNumber: `#`,
    columnFunction: `ఫంక్షన్`,
    columnValue: `విలువ`,
    columnLabel: `లేబుల్`,
    phoneNumberPlaceholder: `ఫోన్ నంబర్`,
    optionalLabelPlaceholder: `ఐచ్ఛిక లేబుల్`,
    extensionNone: `-- ఏదీ లేదు --`,

    // Button function options
    functionBlank: `ఖాళీ`,
    functionLine: `లైన్`,
    functionRollover: `రోల్‌ఓవర్`,
    functionSpeedDial: `స్పీడ్ డయల్`,
    functionPresence: `BLF / ప్రెజెన్స్`,
    functionParkingLot: `పార్కింగ్ లాట్`,
    functionSharedVoicemail: `షేర్డ్ వాయిస్‌మెయిల్`,

    // Preset speed dial options
    presetMangoSupport: `Mango సపోర్ట్`,
    presetLoginQueue: `లాగిన్ క్యూ`,
    presetLogoutQueue: `లాగ్అవుట్ క్యూ`,
    presetBlockCaller: `కాలర్‌ను బ్లాక్ చేయండి`,
    presetOpenClose: `ఓపెన్/క్లోజ్`,
    presetPauseResumeRecording: `రికార్డింగ్ పాజ్/రీస్యూమ్`,
    presetAlternateVm: `ఆల్టర్నేట్ VM`,
    preset911History: `911 హిస్టరీ`,

    // Preset button labels
    presetLabelMangoSupport: `Mango Support`,
    presetLabelLoginQ: `Login Q`,
    presetLabelLogoutQ: `Logout Q`,
    presetLabelBlock: `బ్లాక్`,
    presetLabelOpenClose: `తెరువు/మూసివేయి`,
    presetLabelPauseResume: `పాజ్/కొనసాగించు`,
    presetLabelAltVm: `Alt VM`,
    presetLabel911Hist: `911 Hist`,

    parkingLotOption: `పార్కింగ్ లాట్ {{number}}`,

    multipleLineWarningTitle: `బహుళ లైన్ బటన్లు గుర్తించబడ్డాయి`,
    multipleLineWarning: `బహుళ లైన్ బటన్లను జోడించడం అవాంఛిత ఫలితాలకు దారితీయవచ్చు. మీరు ఎంచుకున్న ఎక్స్‌టెన్షన్ మరో లైన్ బటన్‌కు కేటాయించబడలేదని మరియు వేరే ఏ పరికరంలోనూ కాన్ఫిగర్ చేయబడలేదని నిర్ధారించుకోండి.`,
    multipleLineWarningProceed: `కొనసాగించండి`,
    multipleLineWarningCancel: `రద్దు చేయండి`,
  },

  // ---------------------------------------------------------------------------
  // Clone dialog
  // ---------------------------------------------------------------------------
  clone: {
    title: `పరికరాన్ని క్లోన్ చేయండి`,
    description: `"{{sourceLabel}}" నుండి కొత్త పరికరాన్ని సృష్టించండి. ప్రత్యేకమైన MAC చిరునామాను నమోదు చేయండి. అన్ని సెట్టింగ్‌లు మరియు బటన్లు కొత్త పరికరానికి కాపీ చేయబడతాయి.`,
    loadingSettings: `పరికర సెట్టింగ్‌లు లోడ్ అవుతున్నాయి…`,
    stationLabelPlaceholder: `పరికరం యొక్క క్లోన్`,
    cloneButton: `పరికరాన్ని క్లోన్ చేయండి`,
  },

  // ---------------------------------------------------------------------------
  // Delete dialog
  // ---------------------------------------------------------------------------
  deleteDialog: {
    title: `పరికరాన్ని తొలగించు`,
    confirmation: `మీరు నిజంగా "{{name}}"ని తొలగించాలనుకుంటున్నారా? ఈ చర్యను రద్దు చేయలేరు.`,
    unnamedDevice: `పేరులేని పరికరం`,
  },

  // ---------------------------------------------------------------------------
  // Mass edit dialog
  // ---------------------------------------------------------------------------
  massEdit: {
    title: `పరికరాలను సామూహికంగా సవరించండి`,
    description_one: `{{count}} పరికరాన్ని నవీకరిస్తోంది. ప్రస్తుత విలువను ఉంచడానికి ఫీల్డ్‌ను మార్చకుండా వదిలేయండి.`,

    description_other: `{{count}} పరికరాలను నవీకరిస్తోంది. ప్రస్తుత విలువను ఉంచడానికి ఫీల్డ్‌ను మార్చకుండా వదిలేయండి.`,
    transport: `ట్రాన్స్‌పోర్ట్`,
    natTraversal: `NAT ట్రావర్సల్`,
    jitter: `జిట్టర్`,
    timeZone: `సమయ మండలం`,
    daylightSaving: `డేలైట్ సేవింగ్`,
    allowProvisioning: `ప్రావిజనింగ్‌ను అనుమతించండి`,
    serverGroupHost: `సర్వర్ గ్రూప్ హోస్ట్`,
    saveButton_one: `{{count}} పరికరాన్ని సేవ్ చేయండి`,

    saveButton_other: `{{count}} పరికరాలను సేవ్ చేయండి`,
  },

  // ---------------------------------------------------------------------------
  // Set desired firmware dialog
  // ---------------------------------------------------------------------------
  setFirmware: {
    title: `కావలసిన ఫర్మ్‌వేర్ వెర్షన్‌ను సెట్ చేయండి`,
    description: `{{count}} ఎంచుకున్న పరికరం(లు) కోసం కావలసిన ఫర్మ్‌వేర్ వెర్షన్‌ను సెట్ చేయండి. నవీకరణ తర్వాత కాన్ఫిగరేషన్ మళ్లీ లోడ్ చేయబడుతుంది.`,
    label: `కావలసిన ఫర్మ్‌వేర్ వెర్షన్`,
    placeholder: `ఉదా. 1.2.3.4`,
    confirmButton: `సెట్ చేసి రీలోడ్ చేయండి`,
  },

  // ---------------------------------------------------------------------------
  // List page
  // ---------------------------------------------------------------------------
  list: {
    heading: `పరికరాలు`,
    searchPlaceholder: `పేరు, వినియోగదారు, ఎక్స్‌టెన్షన్, లేదా MAC ద్వారా శోధించండి...`,
    addDevice: `పరికరాన్ని జోడించండి`,
    actionsButton: `చర్యలు`,
    actionsWithCount: `చర్యలు ({{count}} ఎంచుకున్నవి)`,
    massEditAction: `సామూహిక సవరణ ({{count}})`,
    reloadConfigAction: `కాన్ఫిగరేషన్‌ను మళ్లీ లోడ్ చేయండి ({{count}})`,
    reloadConfigRebootAction: `కాన్ఫిగ్ రీలోడ్ & రీబూట్ ({{count}})`,
    setFirmwareAction: `కావలసిన ఫర్మ్‌వేర్‌ను సెట్ చేయండి ({{count}})`,
    rebuildAll: `అన్నింటినీ పునర్నిర్మించండి`,
    rebuildAndReboot: `పునర్నిర్మించండి & రీబూట్ చేయండి`,
    rebuildAllConfirmTitle: `అన్ని పరికరాల కాన్ఫిగ్‌లను పునర్నిర్మించాలా?`,
    rebuildAllConfirmDescription: `మీరు ఈ PBX లోని ప్రతి పరికరం కాన్ఫిగరేషన్‌ను పునర్నిర్మించబోతున్నారు. మీరు ఖచ్చితంగా కొనసాగించాలనుకుంటున్నారా?`,
    rebuildAllConfirmAction: `అన్నింటినీ పునర్నిర్మించండి`,
    rebuildAndRebootConfirmTitle: `అన్ని పరికరాలను పునర్నిర్మించి రీబూట్ చేయాలా?`,
    rebuildAndRebootConfirmDescription: `మీరు ఈ PBX లోని ప్రతి పరికరం కాన్ఫిగరేషన్‌ను పునర్నిర్మించి, వాటిని రీబూట్ చేయబోతున్నారు. మీరు ఖచ్చితంగా కొనసాగించాలనుకుంటున్నారా?`,
    rebuildAndRebootConfirmAction: `పునర్నిర్మాణం & రీబూట్`,
    pbxRequired: `పరికరాల పేజీకి PBX అవసరం.`,
    failedToLoad: `పరికరాలను లోడ్ చేయడంలో విఫలమైంది`,
    noMatchingDevices: `మీ శోధన ప్రమాణాలకు సరిపోలే పరికరాలు లేవు`,

    // Column headers
    columnName: `పేరు`,
    columnPrimaryExt: `ప్రాథమిక ఎక్స్‌టెన్షన్`,
    columnUser: `వినియోగదారు`,
    columnModel: `మోడల్`,
    columnMacAddress: `MAC చిరునామా`,
    columnFirmware: `ఫర్మ్‌వేర్`,
    columnWarranty: `వారంటీ`,
    columnLastCheckIn: `చివరి చెక్ ఇన్`,
    columnInitializedOn: `ఇనిషియలైజ్ చేయబడింది`,
    columnConfiguration: `కాన్ఫిగరేషన్`,
    columnActions: `చర్యలు`,
    selectAllDevices: `అన్ని పరికరాలను ఎంచుకోండి`,
  },

  // ---------------------------------------------------------------------------
  // Device row
  // ---------------------------------------------------------------------------
  row: {
    loadingDevices: `పరికరాలు లోడ్ అవుతున్నాయి...`,
    noDevices: `పరికరాలు లేవు`,
    noDevicesDescription: `ఈ PBX కోసం ఇంకా పరికరాలు లేవు.`,
    selectDevice: `{{name}} ఎంచుకోండి`,
    editDevice: `{{name}} సవరించండి`,
    modelFallback: `మోడల్ {{id}}`,
    reportsModel: `రిపోర్ట్‌లు: {{model}}`,
    warrantyInWarranty: `వారంటీలో`,
    warrantyExpired: `గడువు ముగిసింది`,
    warrantyUnknown: `వారంటీ లేదు`,
    changesPending: `మార్పులు పెండింగ్‌లో`,
    sidecar: `సైడ్‌కార్`,
    noProv: `ప్రావిజన్ లేదు`,
    fromSource: `{{source}} నుండి`,
    desiredFirmware: `(కావలసినది: {{version}})`,
    firmwareUnknown: `తెలియదు`,
    rowActions: `వరుస చర్యలు`,
    cloneAction: `క్లోన్`,
    deleteAction: `తొలగించు`,
  },

  // ---------------------------------------------------------------------------
  // Filters
  // ---------------------------------------------------------------------------
  filters: {
    make: `తయారీదారు`,
    model: `మోడల్`,
    firmware: `ఫర్మ్‌వేర్`,
    firmwarePlaceholder: `ఏదైనా`,
    warranty: `వారంటీ`,
    transport: `ట్రాన్స్‌పోర్ట్`,
    jitter: `జిట్టర్`,
    sidecar: `సైడ్‌కార్`,
    provisioning: `ప్రావిజనింగ్`,
    clearFilters: `ఫిల్టర్‌లను క్లియర్ చేయండి`,
    warrantyUnknown: `వారంటీ లేదు`,
    warrantyInWarranty: `వారంటీలో`,
    warrantyExpired: `గడువు ముగిసింది`,
    provisioningEnabled: `ప్రారంభించబడింది`,
    provisioningDisabled: `నిలిపివేయబడింది`,
    provisioningChangesPending: `మార్పులు పెండింగ్‌లో`,
    toggleFilters: `ఫిల్టర్‌లను టోగుల్ చేయండి`,
  },

  // ---------------------------------------------------------------------------
  // Pagination / toolbar
  // ---------------------------------------------------------------------------
  pagination: {
    resultsPerPage: `పేజీకి ఫలితాలు:`,
    totalCount: `({{count}} మొత్తం)`,
    previousPage: `మునుపటి పేజీ`,
    nextPage: `తదుపరి పేజీ`,
    all: `అన్నీ`,
    jumpToPage: `పేజీ {{page}} కు వెళ్ళండి`,
  },

  // ---------------------------------------------------------------------------
  // Toast messages
  // ---------------------------------------------------------------------------
  toast: {
    deviceCreated: `పరికరం సృష్టించబడింది`,
    createFailed: `పరికరాన్ని సృష్టించడంలో విఫలమైంది`,
    deviceUpdated: `పరికరం నవీకరించబడింది`,
    deviceUpdatedRebooting: `పరికరం నవీకరించబడింది మరియు రీబూట్ అవుతోంది`,
    updateFailed: `పరికరాన్ని నవీకరించడంలో విఫలమైంది`,
  },
} as const satisfies DevicesLocale
